import { createElement, ElementType, FC, ImgHTMLAttributes, useEffect, useState } from 'react';

import { APTypography } from '@astropay/web-core-library';
import { makeStyles } from '@material-ui/core';

import { translateCustomParams } from 'common/config/i18n';
import { sprintf } from 'common/helpers/sprintf';

const useLoaded = ({ src, srcSet }: { src?: string; srcSet?: string }) => {
  const [loaded, setLoaded] = useState<boolean | 'loaded' | 'error'>(false);

  useEffect(() => {
    if (!src && !srcSet) {
      return;
    }

    setLoaded(false);
    let active = true;
    const image = new Image();
    image.src = src ?? '';
    image.srcset = srcSet ?? '';

    image.onload = function () {
      if (!active) {
        return;
      }

      setLoaded('loaded');
    };

    image.onerror = function () {
      if (!active) {
        return;
      }

      setLoaded('error');
    };

    return function () {
      active = false;
    };
  }, [src, srcSet]);
  return loaded;
};

interface ApImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  alt: string;
  src: string;
  srcSet?: string;
  // eslint-disable-next-line
  translationImage?: { term: string; params: any[]; centered?: boolean };
  defaultCmp?: ElementType;
  fallbackClassName?: string;
}

const useStyles = makeStyles(() => ({
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',

    '& > *': {
      margin: '0 5px',
    },
  },
}));

const ApImage: FC<ApImageProps> = ({
  src,
  srcSet,
  alt,
  defaultCmp = 'div',
  children,
  translationImage,
  fallbackClassName,
  ...extraProps
}) => {
  const loaded = useLoaded({
    src,
    srcSet,
  });
  const hasImg = src || srcSet;
  const hasImgNotFailing = hasImg && loaded !== 'error';
  let childrenFail = null;
  const classes = useStyles();

  if (!!translationImage) {
    return (
      <APTypography textStyle="Typography_Poppins_14px_Regular">
        <span
          className={classes.logoContainer}
          dangerouslySetInnerHTML={{
            __html: sprintf(
              translateCustomParams(
                translationImage.term,
                {
                  ...translationImage.params[0],
                  '{merchantName}': `${hasImgNotFailing ? '' : alt}`,
                },
                {
                  '{merchantName}': `${
                    hasImgNotFailing
                      ? `img class=${extraProps.className} src=${src} alt=${alt}`
                      : 'b'
                  }`,
                },
              ),
            ),
          }}
        />
      </APTypography>
    );
  } else if (hasImgNotFailing) {
    return <img src={src} srcSet={srcSet} alt={alt} {...extraProps} />;
  } else if (children) {
    childrenFail = children;
  } else {
    childrenFail = alt;
  }
  return createElement(
    defaultCmp,
    { className: fallbackClassName, style: extraProps.style },
    childrenFail,
  );
};

export default ApImage;
